<template>
    <div class="tab-ne-template-message-form">
        <div class="header">
            <div class="title">{{ $t("whatsapp_business_api.createNewTemplate") }}</div>

            <div class="d-flex gap-5">
                <a-button type="primary" @click="viewTemplates" ghost>
                    <a-icon type="back" />
                    {{ $t("whatsapp_business_api.viewTemplates") }}
                </a-button>
            </div>
        </div>

        <div class="template-message-form-container">
            <div class="inputs">
                <div class="input-wrapper">
                    <div class="input-header">
                        <label>
                            {{ $t("whatsapp_business_api.templateName") }}
                            <span class="required">{{ $t("message.required") }}</span>
                        </label>
                        <a-icon type="question-circle" />
                    </div>
                    <span class="description">{{ $t("whatsapp_business_api.templateNameDesc") }} </span>
                    <a-input ref="elementNameInput" v-model="form.elementName" :class="{ error: elementNameError }" />
                    <span class="input-desc">
                        {{ $t("whatsapp_business_api.templateNameRules") }}
                    </span>
                </div>
                <div class="input-wrapper">
                    <div class="input-header">
                        <label>
                            {{ $t("whatsapp_business_api.category") }}
                            <span class="required">{{ $t("message.required") }}</span>
                        </label>
                        <a-icon type="question-circle" />
                    </div>
                    <span class="description">{{ $t("whatsapp_business_api.categoryDesc") }}</span>
                    <div class="categories">
                        <div
                            class="category"
                            v-for="category of categories"
                            :key="category.id"
                            @click="form.category = category.id"
                        >
                            <div class="category-header">
                                <div class="left">
                                    <span class="material-icons" v-if="form.category === category.id">
                                        radio_button_checked
                                    </span>
                                    <span class="material-icons" v-else> radio_button_unchecked </span>
                                    <span class="label">{{ category.label }}</span>
                                </div>
                                <div class="right">
                                    <span class="material-icons"> {{ category.icon }} </span>
                                </div>
                            </div>
                            <div class="category-description">
                                {{ category.description }}
                            </div>
                        </div>
                    </div>

                    <div class="category-types" v-if="categories.find((c) => c.id === form.category).types.length > 0">
                        <div
                            class="category-type"
                            @click="selectTemplateType(categoryType)"
                            v-for="categoryType of categories.find((c) => c.id === form.category).types"
                        >
                            <div class="category-type-header">
                                <div class="left">
                                    <span class="material-icons" v-if="form.templateType === categoryType">
                                        radio_button_checked
                                    </span>
                                    <span class="material-icons" v-else> radio_button_unchecked </span>
                                    <span class="label">{{ categoryTypes[categoryType].label }}</span>
                                </div>
                            </div>
                            <div class="category-type-description">
                                {{ categoryTypes[categoryType].description }}
                            </div>
                        </div>
                    </div>
                </div>
                <div class="input-wrapper">
                    <div class="input-header">
                        <label>
                            {{ $t("settings.language") }}
                            <span class="required">{{ $t("message.required") }}</span>
                        </label>
                        <a-icon type="question-circle" />
                    </div>
                    <span class="description">{{ $t("whatsapp_business_api.languageDesc") }}</span>
                    <a-select v-model="form.languageCode" style="width: 100%">
                        <a-select-option v-for="language of languages" :key="language.code" :value="language.code">
                            {{ language.label }}
                        </a-select-option>
                    </a-select>
                </div>
                <div class="input-wrapper" v-if="form.templateType === 'CUSTOM_MESSAGE'">
                    <div class="input-header">
                        <label>
                            {{ $t("whatsapp_business_api.header") }}
                            <span class="optional">{{ $t("whatsapp_business_api.optional") }}</span>
                        </label>
                        <a-icon type="question-circle" />
                    </div>
                    <span class="description">
                        {{ $t("whatsapp_business_api.headerRules") }}
                    </span>

                    <div class="d-flex gap-10">
                        <label style="width: 100px">{{ $t("whatsapp_business_api.headerType") }}</label>
                        <a-select v-model="form.customMessageType" style="width: 100%">
                            <a-select-option
                                v-for="templateType of customMessageTypes"
                                :key="templateType.id"
                                :value="templateType.id"
                            >
                                {{ templateType.label }}
                            </a-select-option>
                        </a-select>
                    </div>
                    <template v-if="form.customMessageType === 'TEXT'">
                        <div class="d-flex gap-10">
                            <label style="width: 100px">{{ $t("whatsapp_business_api.headerText") }}</label>
                            <a-input
                                v-model="form.header"
                                :placeholder="$t('whatsapp_business_api.header')"
                                :max-length="60"
                                @input="headerChanged"
                            />
                        </div>
                        <div class="input-footer">
                            <div class="counter">
                                <span>{{ (form.header || "").length }}</span>
                                <span>/</span>
                                <span>60</span>
                            </div>
                        </div>
                        <div class="variables-container" v-if="headerVariables.length">
                            <div class="labels">
                                <label>{{ $t("whatsapp_business_api.variables") }}</label>
                                <label id="labelSecond">{{ $t("whatsapp_business_api.defaultVariableName") }}</label>
                                <a-icon
                                    type="question-circle"
                                    style="cursor: default"
                                    v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                    :content="$t('whatsapp_business_api.defaultVariableToolTip')"
                                />
                            </div>
                            <div class="variables">
                                <div class="variable" v-for="(variable, i) of headerVariables">
                                    <a-input
                                        v-model="headerVariables[i].variable"
                                        :placeholder="$t('whatsapp_business_api.variable')"
                                        :disabled="true"
                                    />
                                    <a-input
                                        v-model="headerVariables[i].example"
                                        :placeholder="$t('whatsapp_business_api.example')"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="add-variable-button">
                            <a-button type="primary" @click="addHeaderVariable" ghost>
                                <a-icon type="plus" />
                                {{ $t("whatsapp_business_api.addVariable") }}
                            </a-button>
                        </div>
                    </template>
                </div>
                <div
                    class="input-wrapper"
                    v-if="
                        form.templateType === 'CUSTOM_MESSAGE' &&
                        ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(form.customMessageType)
                    "
                >
                    <div class="input-header">
                        <label>
                            Media
                            <span class="optional">{{ $t("whatsapp_business_api.optional") }}</span>
                        </label>
                        <a-icon type="question-circle" />
                    </div>
                    <div class="media-preview" v-if="media">
                        <div class="media-preview-content">
                            <div class="media-image">
                                <img :src="media.data" alt="" />
                            </div>
                            <div class="media-preview-name">
                                {{ media.name }}
                            </div>
                        </div>
                        <div class="media-preview-delete" @click="media = null">
                            <div class="material-icons">delete</div>
                        </div>
                    </div>
                    <div class="media-drag" v-else>
                        <div class="input">
                            <input
                                type="file"
                                id="media-source"
                                accept="image/jpeg, image/png, video/mp4, application/pdf"
                                @change="handleFileUpload"
                            />
                        </div>
                        <div class="material-icons">image</div>
                        <div class="content">Click here to upload a picture.</div>
                    </div>
                    <span class="description">
                        <div class="input-warn">
                            <div>
                                <b>Note:</b>Following are the file formats and their respective sizes which are accepted
                                for upload
                            </div>
                            <ul class="list-item">
                                <li>Image: JPEG and PNG (Max size 2 MB)</li>
                                <li>Video: MP4 (Max size 20 MB)</li>
                                <li>Document: (Max size 2 MB)</li>
                            </ul>
                        </div>
                    </span>
                </div>
                <div class="input-wrapper">
                    <div class="input-header">
                        <label>
                            {{ $t("whatsapp_business_api.body") }}
                            <span class="required">{{ $t("message.required") }}</span>
                        </label>
                        <a-icon type="question-circle" />
                    </div>
                    <span class="description">{{ $t("whatsapp_business_api.bodyRules") }}.</span>
                    <a-textarea v-model="form.content" :auto-size="{ minRows: 1, maxRows: 15 }" :max-length="1024" />

                    <div class="input-footer">
                        <div class="counter">
                            <span>{{ form.content.length }}</span>
                            <span>/</span>
                            <span>1024</span>
                        </div>
                    </div>
                    <div class="variables-container" v-if="bodyVariables.length">
                        <div class="labels">
                            <label>{{ $t("whatsapp_business_api.variables") }}</label>
                            <label id="labelSecond">{{ $t("whatsapp_business_api.defaultVariableName") }}</label>
                            <a-icon
                                type="question-circle"
                                style="cursor: default"
                                v-tippy="{ followCursor: true, placement: 'bottom-end' }"
                                :content="$t('whatsapp_business_api.defaultVariableToolTip')"
                            />
                        </div>

                        <div class="variables">
                            <div class="variable" v-for="(variable, i) of bodyVariables">
                                <a-input
                                    v-model="bodyVariables[i].variable"
                                    :placeholder="$t('whatsapp_business_api.variable')"
                                    :disabled="true"
                                />
                                <a-input
                                    v-model="bodyVariables[i].example"
                                    :placeholder="$t('whatsapp_business_api.example')"
                                />
                            </div>
                        </div>
                    </div>
                    <div class="add-variable-button">
                        <a-button type="primary" @click="addVariable" ghost>
                            <a-icon type="plus" />
                            {{ $t("whatsapp_business_api.addVariable") }}
                        </a-button>
                    </div>
                </div>
                <div class="input-wrapper" v-if="form.templateType === 'CUSTOM_MESSAGE'">
                    <div class="input-header">
                        <label>
                            {{ $t("whatsapp_business_api.footer") }}
                            <span class="optional">{{ $t("whatsapp_business_api.optional") }}</span>
                        </label>
                        <a-icon type="question-circle" />
                    </div>
                    <span class="description">
                        {{ $t("whatsapp_business_api.footerDesc") }}
                    </span>
                    <a-input v-model="form.footer" placeholder="Footer" :max-length="60" />

                    <div class="input-footer">
                        <div class="counter">
                            <span>{{ (form.footer || "").length }}</span>
                            <span>/</span>
                            <span>60</span>
                        </div>
                    </div>
                </div>
                <div class="input-wrapper" v-if="form.templateType === 'CUSTOM_MESSAGE'">
                    <div class="input-header">
                        <label>
                            {{ $t("whatsapp_business_api.buttons") }}
                            <span class="optional">{{ $t("whatsapp_business_api.optional") }}</span>
                        </label>
                        <a-icon type="question-circle" />
                    </div>

                    <div class="d-flex gap-10">
                        <label style="width: 100px">{{ $t("whatsapp_business_api.buttonType") }}</label>
                        <a-select v-model="form.buttonType" style="width: 100%">
                            <a-select-option
                                v-for="(buttonType, index) of buttonTypes"
                                :key="index"
                                :value="buttonType.id"
                            >
                                {{ buttonType.label }}
                            </a-select-option>
                        </a-select>
                    </div>
                    <a-divider />
                    <div class="d-flex flex-column gap-10" v-if="form.buttonType !== 'NONE'">
                        <div class="input-footer">
                            <a-button type="primary" @click="addButton" ghost :disabled="form.buttons.length >= 10">
                                <a-icon type="plus" />
                                {{ $t("whatsapp_business_api.addButton") }}
                            </a-button>
                            <div class="counter">
                                <span>{{ form.buttons.length }}</span>
                                <span>/</span>
                                <span>10</span>
                            </div>
                        </div>
                        <div class="input-warn">
                            <div>
                                <b>{{ $t("userInfoWidget.note") }}: </b>{{ $t("message.templateButtonNote") }}
                            </div>
                            <ul class="list-item">
                                <li>
                                    <b>{{ $t("message.templateButtonTags") }}</b>
                                </li>
                            </ul>
                        </div>
                        <div class="button-container">
                            <label>{{ $t("whatsapp_business_api.buttons") }}</label>
                            <div class="button-management">
                                <!-- Button Preview List -->
                                <div class="button-preview-section">
                                    <div class="button-previews">
                                        <draggable v-model="form.buttons" handle=".drag-handle" @end="onButtonDragEnd">
                                            <div 
                                                v-for="(button, index) in form.buttons" 
                                                :key="index"
                                                :class="['button-preview-item', { active: selectedButtonIndex === index }]"
                                                @click="selectedButtonIndex = index"
                                            >
                                                <div class="button-preview">
                                                    <supsis-button style="color: rgb(0, 165, 244)">
                                                        {{ button.text || `Button ${index + 1}` }}
                                                    </supsis-button>
                                                    <div class="button-type-badge">{{ button.type }}</div>
                                                </div>
                                                <div class="button-preview-actions">
                                                    <span class="material-icons drag-handle">drag_indicator</span>
                                                    <span class="material-icons remove-button" @click.stop="removeButtonList(button)">close</span>
                                                </div>
                                            </div>
                                        </draggable>
                                    </div>
                                </div>

                                <!-- Button Configuration -->
                                <div class="button-config-section" v-if="form.buttons.length > 0 && selectedButtonIndex >= 0">
                                    <div class="config-header">
                                        <h3>Configure Button {{ selectedButtonIndex + 1 }}</h3>
                                        <div class="button-type">{{ (form.buttons[selectedButtonIndex] && form.buttons[selectedButtonIndex].type) || 'Unknown' }}</div>
                                    </div>

                                    <!-- URL Button Config -->
                                    <template v-if="form.buttons[selectedButtonIndex] && form.buttons[selectedButtonIndex].type === 'URL'">
                                        <div class="button-config-form">
                                            <div class="config-grid">
                                                <div class="config-field">
                                                    <label>Button Text</label>
                                                    <a-input
                                                        v-model="form.buttons[selectedButtonIndex].text"
                                                        placeholder="Enter button text"
                                                        :max-length="25"
                                                    />
                                                    <div class="field-info">{{ (form.buttons[selectedButtonIndex].text && form.buttons[selectedButtonIndex].text.length) || 0 }}/25</div>
                                                </div>

                                                <div class="config-field">
                                                    <label>Select URL type</label>
                                                    <a-select v-model="form.buttons[selectedButtonIndex].urlType">
                                                        <a-select-option value="STATIC">Static</a-select-option>
                                                        <a-select-option value="DYNAMIC">Dynamic</a-select-option>
                                                    </a-select>
                                                </div>
                                            </div>

                                            <div class="config-field">
                                                <label>Button Value</label>
                                                <a-input 
                                                    v-model="form.buttons[selectedButtonIndex].url" 
                                                    v-if="form.buttons[selectedButtonIndex].urlType === 'STATIC'"
                                                    placeholder="https://supsis.com/" 
                                                />
                                                <a-input 
                                                    v-model="form.buttons[selectedButtonIndex].buttonValue" 
                                                    v-else
                                                    placeholder="https://supsis.com/"
                                                />
                                            </div>

                                            <div v-if="form.buttons[selectedButtonIndex].urlType === 'DYNAMIC'" class="dynamic-config">
                                                <div class="dynamic-info">
                                                    <span class="material-icons">info</span>
                                                    <span>Sample dynamic URL with variable (Note: Do not add the URL in your sample message body above)</span>
                                                </div>
                                                <div class="dynamic-input-group">
                                                    <span class="variable-tag" v-text="'{{1}}'"></span>
                                                    <a-input
                                                        v-model="form.buttons[selectedButtonIndex].suffix"
                                                        placeholder="test"
                                                        style="flex: 1"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </template>

                                    <!-- Phone Number Button Config -->
                                    <template v-if="form.buttons[selectedButtonIndex] && form.buttons[selectedButtonIndex].type === 'PHONE_NUMBER'">
                                        <div class="button-config-form">
                                            <div class="config-field">
                                                <label>Button Text</label>
                                                <a-input
                                                    v-model="form.buttons[selectedButtonIndex].text"
                                                    placeholder="Call Us"
                                                    :max-length="25"
                                                />
                                                <div class="field-info">{{ (form.buttons[selectedButtonIndex].text && form.buttons[selectedButtonIndex].text.length) || 0 }}/25</div>
                                            </div>
                                            <div class="config-field">
                                                <label>Phone Number</label>
                                                <a-input 
                                                    v-model="form.buttons[selectedButtonIndex].phone_number" 
                                                    placeholder="Phone Number" 
                                                    v-mask="'###############'" 
                                                />
                                            </div>
                                        </div>
                                    </template>

                                    <!-- Quick Reply Button Config -->
                                    <template v-if="form.buttons[selectedButtonIndex] && form.buttons[selectedButtonIndex].type === 'QUICK_REPLY'">
                                        <div class="button-config-form">
                                            <div class="config-field">
                                                <label>Button Text</label>
                                                <a-input
                                                    v-model="form.buttons[selectedButtonIndex].text"
                                                    placeholder="Quick Reply"
                                                    :max-length="25"
                                                />
                                                <div class="field-info">{{ (form.buttons[selectedButtonIndex].text && form.buttons[selectedButtonIndex].text.length) || 0 }}/25</div>
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="input-wrapper" v-if="form.templateType === 'CAROUSEL'">
                    <div class="input-header">
                        <label>
                            {{ $t("whatsapp_business_api.carousel") }}
                            <span class="required">{{ $t("message.required") }}</span>
                        </label>
                        <a-icon type="question-circle" />
                    </div>
                    <span class="description">{{ $t("whatsapp_business_api.carouselDesc") }}</span>

                    <div class="global-settings">
                        <div class="media-type-section">
                            <div class="section-header">
                                <label>Media Type</label>
                                <span class="required">{{ $t("message.required") }}</span>
                            </div>
                            <a-select v-model="carouselMediaType" style="width: 100%" @change="handleMediaTypeChange">
                                <a-select-option value="IMAGE">Image</a-select-option>
                                <a-select-option value="VIDEO">Video</a-select-option>
                            </a-select>
                            <span class="description">
                                <div class="input-warn">
                                    <div>
                                        <b>Note:</b> All cards will use the same media type. Supported formats and
                                        sizes:
                                    </div>
                                    <ul class="list-item">
                                        <li>Image: JPEG and PNG (Max size 2 MB)</li>
                                        <li>Video: MP4 (Max size 20 MB)</li>
                                    </ul>
                                </div>
                            </span>
                        </div>

                        <div class="button-config-section">
                            <div class="section-header">
                                <label>Buttons Configuration</label>
                                <span class="optional">{{ $t("whatsapp_business_api.optional") }}</span>
                            </div>
                            <div class="button-config">
                                <div v-for="(button, index) in carouselButtons" :key="index" class="button-type-config">
                                    <div class="button-type">
                                        <label class="button-label">Button {{ index + 1 }} Type</label>
                                        <a-select v-model="button.type" style="width: 100%" @change="updateCardButtons">
                                            <a-select-option value="QUICK_REPLY">Quick Reply</a-select-option>
                                            <a-select-option value="URL">URL</a-select-option>
                                        </a-select>
                                    </div>
                                    <div class="button-delete" v-if="index === carouselButtons.length - 1">
                                        <a-button
                                            type="link"
                                            @click="removeGlobalButton"
                                            :disabled="carouselButtons.length === 0"
                                        >
                                            <a-icon type="delete" />
                                        </a-button>
                                    </div>
                                </div>
                                <div class="button-add">
                                    <a-button
                                        @click="addGlobalButton"
                                        :disabled="carouselButtons.length >= 2"
                                        type="primary"
                                        ghost
                                    >
                                        <a-icon type="plus" />
                                        Add Button
                                    </a-button>
                                    <span class="counter">{{ carouselButtons.length }}/2 buttons</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="carousel-cards-section">
                        <draggable v-model="carouselCards" handle=".drag-handle">
                            <div v-for="(card, index) in carouselCards" :key="index" class="carousel-card">
                                <div class="card-header" @click="toggleCard(index)">
                                    <div class="left">
                                        <span class="drag-handle">☰</span>
                                        <span class="carousel-card-heading">Card {{ index + 1 }}</span>
                                    </div>
                                    <div class="right">
                                        <a-button
                                            type="link"
                                            class="delete-btn"
                                            @click.stop="removeCard(index)"
                                            :disabled="carouselCards.length === 1"
                                        >
                                            <a-icon type="delete" />
                                        </a-button>
                                        <a-icon :type="card.isOpen ? 'up' : 'down'" />
                                    </div>
                                </div>

                                <div class="card-content" v-show="card.isOpen">
                                    <div class="media-section">
                                        <div class="media-upload">
                                            <div class="media-preview" v-if="card.media">
                                                <div class="media-preview-content">
                                                    <div class="media-image">
                                                        <img :src="card.media.data" alt="" />
                                                    </div>
                                                    <div class="media-preview-name">
                                                        {{ card.media.name }}
                                                    </div>
                                                </div>
                                                <div class="media-preview-delete" @click="removeCardMedia(index)">
                                                    <div class="material-icons">close</div>
                                                </div>
                                            </div>
                                            <div class="media-drag" v-else>
                                                <div class="input">
                                                    <input
                                                        type="file"
                                                        :accept="getAcceptedFileTypes(carouselMediaType)"
                                                        @change="(event) => handleCardMediaUpload(event, index)"
                                                    />
                                                </div>
                                                <div class="material-icons">image</div>
                                                <div class="content">
                                                    Click here to upload {{ carouselMediaType.toLowerCase() }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="description-section">
                                        <div class="section-header">
                                            <label>Description</label>
                                            <span class="required">{{ $t("message.required") }}</span>
                                        </div>
                                        <a-textarea
                                            v-model="card.body"
                                            :auto-size="{ minRows: 2, maxRows: 4 }"
                                            :maxLength="160"
                                            @input="cardBodyChanged(index)"
                                        />
                                        <div class="description-footer">
                                            <span>{{ card.body.length }}/160</span>
                                        </div>

                                        <div class="variables-container" v-if="card.variables.length">
                                            <div class="variables">
                                                <div class="variable" v-for="(variable, i) in card.variables" :key="i">
                                                    <a-input v-model="card.variables[i].variable" :disabled="true" />
                                                    <a-input
                                                        v-model="card.variables[i].example"
                                                        :placeholder="$t('whatsapp_business_api.example')"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div class="variable-button">
                                            <a-button type="primary" @click="addCardVariable(index)" ghost>
                                                <a-icon type="plus" />
                                                {{ $t("whatsapp_business_api.addVariable") }}
                                            </a-button>
                                        </div>
                                    </div>

                                    <div class="buttons-section" v-if="carouselButtons.length > 0">
                                        <div class="button-list">
                                            <div
                                                v-for="(button, buttonIndex) in card.buttons"
                                                :key="buttonIndex"
                                                class="button-item"
                                            >
                                                <div class="button-inputs-row">
                                                    <div class="input-group">
                                                        <label>Select type</label>
                                                        <a-input :value="button.type" disabled />
                                                    </div>

                                                    <div class="input-group">
                                                        <label>Button Title</label>
                                                        <a-input
                                                            v-model="button.text"
                                                            :placeholder="`Button ${buttonIndex + 1} Text`"
                                                        />
                                                    </div>

                                                    <div class="input-group" v-if="button.type === 'URL'">
                                                        <label>Select URL type</label>
                                                        <a-select v-model="button.urlType" style="width: 100%">
                                                            <a-select-option value="STATIC">Static</a-select-option>
                                                            <a-select-option value="DYNAMIC">Dynamic</a-select-option>
                                                        </a-select>
                                                    </div>

                                                    <div class="input-group" v-if="button.type === 'URL'">
                                                        <label>Button Value</label>
                                                        <a-input
                                                            v-model="button.url"
                                                            v-if="button.urlType === 'STATIC'"
                                                            placeholder="Enter static URL"
                                                        />
                                                        <a-input v-model="button.buttonValue" v-else />
                                                    </div>
                                                </div>
                                                <div
                                                    class="button-inputs-row"
                                                    v-if="button.type === 'URL' && button.urlType === 'DYNAMIC'"
                                                >
                                                    <div class="sample-url-section">
                                                        <div class="sample-url-inputs">
                                                            <span>
                                                                {{ "{" + "{" + "1" + "}" + "}" }}
                                                            </span>
                                                            <a-input
                                                                v-model="button.suffix"
                                                                placeholder="Type Sample Suffix"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </draggable>
                    </div>

                    <div class="carousel-actions">
                        <a-button type="primary" @click="addCard" :disabled="carouselCards.length >= 10" ghost>
                            <a-icon type="plus" />
                            Add Card
                        </a-button>
                        <span class="counter">{{ carouselCards.length }}/10 cards</span>
                    </div>
                </div>

                <div class="actions d-flex flex-end w-100">
                    <a-button type="primary" @click="submit" :disabled="submitting">
                        <a-icon type="save" />
                        {{ submitting ? $t("whatsapp_business_api.submitting") : $t("whatsapp_business_api.submit") }}
                    </a-button>
                </div>
            </div>
            <div class="right">
                <div class="preview-container">
                    <WhatsappMessagePreview
                        :body="getExampleBodyContent(form.content, bodyVariables)"
                        :footer="form.footer"
                        :template-type="form.templateType"
                        :header-media-type="form.customMessageType"
                        :header-text="getExampleBodyContent(form.header, headerVariables)"
                        :buttons="form.buttons"
                        :cards="formatCards()"
                        :headerMediaURL="media && media.asset.url"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Vue from "vue";
import WABusinessMenuTypes from "../WABusinessMenuTypes";
import log2 from "../../../../../utils/log2";
import { sendSocketMessage, socketMessageStream } from "../../../../../utils/socketStream";
import { uploadAsset } from "../../../../../api/assetUpload";
import axios from "axios";
import WhatsappMessagePreview from "../../WhatsappMessagePreview.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import draggable from "vuedraggable";
import { message } from "ant-design-vue";

const log = log2("TemplateMessageForm.vue");
export default Vue.extend({
    name: "TemplateMessageForm",
    components: { SupsisButton, WhatsappMessagePreview, draggable },
    props: {
        channel: {
            required: true,
        },
        changePage: {
            type: Function,
            default: () => {
            },
        },
    },
    data() {
        return {
            elementNameError: false,
            customMessageTypes: [
                {
                    id: "NONE",
                    label: this.$t("whatsapp_business_api.none"),
                },
                {
                    id: "TEXT",
                    label: this.$t("whatsapp_business_api.headerOptionText"),
                },
                {
                    id: "IMAGE",
                    label: "Image",
                },
                {
                    id: "VIDEO",
                    label: "Video",
                },
                {
                    id: "DOCUMENT",
                    label: "Document",
                },
            ],
            buttonTypes: [],
            staticButtonTypes: [
                {
                    id: "NONE",
                    label: this.$t("whatsapp_business_api.none"),
                },
                {
                    id: "QUICK_REPLY",
                    label: this.$t("whatsapp_business_api.buttonTypeReply"),
                },
                {
                    id: "URL",
                    label: "URL",
                    limit: 3,
                },
                {
                    id: "PHONE_NUMBER",
                    label: this.$t("message.phoneNumber"),
                    limit: 1,
                },
                {
                    id: "BAN_CONTACT",
                    label: this.$t("message.ban"),
                },
            ],
            categories: [
                {
                    id: "UTILITY",
                    label: this.$t("whatsapp_business_api.categoryUtility"),
                    description: this.$t("whatsapp_business_api.categoryUtilityDesc"),
                    icon: "work",
                    types: ["CUSTOM_MESSAGE", "CAROUSEL"],
                },
                {
                    id: "MARKETING",
                    label: this.$t("whatsapp_business_api.categoryMarketing"),
                    description: this.$t("whatsapp_business_api.categoryMarketingDesc"),
                    icon: "campaign",
                    types: ["CUSTOM_MESSAGE", "CAROUSEL"],
                },
                {
                    id: "AUTHENTICATION",
                    label: this.$t("whatsapp_business_api.categoryAuth"),
                    description: this.$t("whatsapp_business_api.categoryAuthDesc"),
                    icon: "password",
                    types: [],
                },
            ],
            categoryTypes: {
                CUSTOM_MESSAGE: {
                    label: "Custom Message",
                    description:
                        "Send promotional offers, announcements and more to increase awareness and engagement.",
                },
                CAROUSEL: {
                    label: "Carousel",
                    description: "You can send up to 10 carousel cards with images or videos",
                    tooltip: "Feature supported for only Meta cloud hosted businesses.",
                },
            },
            languages: [
                { label: "Afrikaans", code: "af" },
                { label: "Albanian", code: "sq" },
                { label: "Arabic", code: "ar" },
                { label: "Azerbaijani", code: "az" },
                { label: "Bengali", code: "bn" },
                { label: "Bulgarian", code: "bg" },
                { label: "Catalan", code: "ca" },
                { label: "Chinese (CHN)", code: "zh_CN" },
                { label: "Chinese (HKG)", code: "zh_HK" },
                { label: "Chinese (TAI)", code: "zh_TW" },
                { label: "Croatian", code: "hr" },
                { label: "Czech", code: "cs" },
                { label: "Danish", code: "da" },
                { label: "Dutch", code: "nl" },
                { label: "English", code: "en" },
                { label: "English (UK)", code: "en_GB" },
                { label: "English (US)", code: "en_US" },
                { label: "Estonian", code: "et" },
                { label: "Filipino", code: "fil" },
                { label: "Finnish", code: "fi" },
                { label: "French", code: "fr" },
                { label: "Georgian", code: "ka" },
                { label: "German", code: "de" },
                { label: "Greek", code: "el" },
                { label: "Gujarati", code: "gu" },
                { label: "Hausa", code: "ha" },
                { label: "Hebrew", code: "he" },
                { label: "Hindi", code: "hi" },
                { label: "Hungarian", code: "hu" },
                { label: "Indonesian", code: "id" },
                { label: "Irish", code: "ga" },
                { label: "Italian", code: "it" },
                { label: "Japanese", code: "ja" },
                { label: "Kannada", code: "kn" },
                { label: "Kazakh", code: "kk" },
                { label: "Kinyarwanda", code: "rw_RW" },
                { label: "Korean", code: "ko" },
                { label: "Kyrgyz (Kyrgyzstan)", code: "ky_KG" },
                { label: "Lao", code: "lo" },
                { label: "Latvian", code: "lv" },
                { label: "Lithuanian", code: "lt" },
                { label: "Macedonian", code: "mk" },
                { label: "Malay", code: "ms" },
                { label: "Malayalam", code: "ml" },
                { label: "Marathi", code: "mr" },
                { label: "Norwegian", code: "nb" },
                { label: "Persian", code: "fa" },
                { label: "Polish", code: "pl" },
                { label: "Portuguese (BR)", code: "pt_BR" },
                { label: "Portuguese (POR)", code: "pt_PT" },
                { label: "Punjabi", code: "pa" },
                { label: "Romanian", code: "ro" },
                { label: "Russian", code: "ru" },
                { label: "Serbian", code: "sr" },
                { label: "Slovak", code: "sk" },
                { label: "Slovenian", code: "sl" },
                { label: "Spanish", code: "es" },
                { label: "Spanish (ARG)", code: "es_AR" },
                { label: "Spanish (SPA)", code: "es_ES" },
                { label: "Spanish (MEX)", code: "es_MX" },
                { label: "Swahili", code: "sw" },
                { label: "Swedish", code: "sv" },
                { label: "Tamil", code: "ta" },
                { label: "Telugu", code: "te" },
                { label: "Thai", code: "th" },
                { label: "Turkish", code: "tr" },
                { label: "Ukrainian", code: "uk" },
                { label: "Urdu", code: "ur" },
                { label: "Uzbek", code: "uz" },
                { label: "Vietnamese", code: "vi" },
                { label: "Zulu", code: "zu" },
            ],
            form: {
                templateType: "CUSTOM_MESSAGE",
                category: "MARKETING",
                elementName: "new_campaign_1",
                languageCode: "tr",
                content: "Merhaba {{1}}, yeni ürünlerimizden haberdar olmak ister misiniz?",
                appId: this.channel.payload.appId,
                customMessageType: "TEXT",
                buttonType: "NONE",
                vertical: "new_campaign_1",
                example: "",
                allowTemplateCategoryChange: true,
                checkerApprovalRequired: false,
                enableSample: true,
                buttons: [],
                header: "",
                exampleHeader: null,
                exampleMedia: null,
                footer: "iletişim için teşekkürler",
            },
            bodyVariables: [],
            submitting: false,
            media: null,
            carouselCards: [
                {
                    isOpen: true,
                    headerType: "IMAGE",
                    media: null,
                    mediaUrl: null,
                    mediaId: null,
                    exampleMedia: null,
                    body: "",
                    sampleText: "",
                    buttons: [
                        {
                            type: "QUICK_REPLY",
                            text: "Default Button",
                        },
                    ],
                    variables: [],
                },
            ],
            carouselButtons: [
                {
                    type: "QUICK_REPLY",
                    text: "Default Button",
                },
            ],
            headerVariables: [],
            selectedButtonIndex: -1,
        };
    },
    computed: {
        carouselMediaType: {
            get() {
                return this.form.customMessageType || "IMAGE";
            },
            set(value) {
                this.form.customMessageType = value;
            },
        },
    },
    methods: {
        headerChanged() {
            const variables = _.uniq(this.form.header.match(/\{\{(\d+)\}\}/g) || []);
            const headerVariableHash = _.keyBy(this.headerVariables, "variable");
            this.headerVariables = variables.map((variable) => {
                return {
                    variable,
                    example: _.get(headerVariableHash, [variable, "example"], ""),
                };
            });
        },
        cardBodyChanged(cardIndex) {
            const card = this.carouselCards[cardIndex];
            const cardVariablesHash = _.keyBy(card.variables, "variable");
            this.carouselCards = this.carouselCards.map((c, i) => {
                if (i === cardIndex) {
                    const variables = _.uniq(card.body.match(/\{\{(\d+)\}\}/g) || []);
                    const newVariables = variables.map((variable) => {
                        return {
                            variable,
                            example: _.get(cardVariablesHash, [variable, "example"], ""),
                        };
                    });
                    return {
                        ...card,
                        variables: newVariables,
                    };
                }
                return c;
            });
        },
        selectTemplateType(type) {
            this.form.templateType = type;
            if (type === "CUSTOM_MESSAGE") {
                this.form.customMessageType = "TEXT";
            } else if (type === "CAROUSEL") {
                this.form.customMessageType = "IMAGE";
            }
        },
        formatCards() {
            /**
             *  {
             *         headerType: "IMAGE",
             *         mediaUrl: "https://www.buildquickbots.com/whatsapp/media/sample/jpg/sample01.jpg",
             *         mediaId: null,
             *         exampleMedia: null,
             *         body: "New Year is round the corner {{1}}",
             *         sampleText: "New Year is round the corner User",
             *         buttons: [
             *             {
             *                 type: "URL",
             *                 text: "Buy now",
             *                 url: "https://www.luckyshrub.com/shop?promo={{1}}",
             *                 buttonValue: "https://www.luckyshrub.com/shop?promo=",
             *                 suffix: "exotic_produce_2023",
             *                 example: ["https://www.luckyshrub.com/shop?promo=exotic_produce_2023"],
             *             },
             *             { type: "QUICK_REPLY", text: "Send more like this" },
             *         ],
             *     },
             */
            return this.carouselCards.map((card) => {
                const variablesHash = _.keyBy(card.variables, "variable");
                const sampleText = card.body.replace(/\{\{(\d+)\}\}/g, (match, capture) => {
                    return _.get(variablesHash, `[${match}].example`, match);
                });
                return {
                    sampleText,
                    headerType: card.headerType,
                    mediaUrl: card.mediaUrl || card.media?.asset?.url,
                    mediaId: null,
                    exampleMedia: card.mediaUrl || card.media?.asset?.url,
                    body: card.body,
                    buttons: card.buttons.map((button) => {
                        return {
                            type: button.type,
                            text: button.text,
                            url: button.buttonValue + button.urlType === "STATIC" ? "" : "{{1}}",
                            buttonValue: button.buttonValue,
                            suffix: button.suffix,
                            example: [button.buttonValue + button.urlType === "STATIC" ? "" : button.suffix],
                        };
                    }),
                    variables: card.variables.map((variable) => {
                        return {
                            variable: variable.variable,
                            example: variable.example,
                        };
                    }),
                };
            });
        },
        checkIsValid() {
            if (this.elementNameError) {
                this.$message.error(this.$t("message.templateNameIsNotValid"));
                const elementPosition = this.$refs.elementNameInput.$el.offsetTop - 100;
                window.scrollTo({ top: elementPosition, behavior: "smooth" });
                return false;
            }
            const assetValid =
                this.form.templateType === "CAROUSEL"
                    ? this.carouselCards.every((card) => card.mediaUrl)
                    : !["NONE", "TEXT"].includes(this.form.customMessageType)
                        ? this.form.exampleMedia
                        : true;
            if (!assetValid) {
                this.$message.error("Please upload media for the template");
                return false;
            }

            const headerVariables = _.uniq(this.form.header.match(/\{\{(\d+)\}\}/g) || []);
            if (headerVariables.length > 1) {
                this.$message.error("Header can have only one variable");
                return false;
            }

            const bodyVariables = _.uniq(this.form.content.match(/\{\{(\d+)\}\}/g) || []);
            if (bodyVariables.length > 4) {
                this.$message.error("Body can have maximum 4 variables");
                return false;
            }

            return true;
        },
        async handleFileUpload(event) {
            const file = event.target.files[0];
            if (file) {
                const imageMime = ["image/jpeg", "image/png"];
                const videoMime = ["video/mp4"];
                const pdfMimeType = ["application/pdf"];
                const mb = file.size / 1024 / 1024;
                if (imageMime.includes(file.type) && mb >= 5) {
                    this.$message.error("The image could not be uploaded because it is more than 5 MB.", 3);
                    return;
                }
                if (videoMime.includes(file.type) && mb >= 16) {
                    this.$message.error("The video could not be uploaded because it is more than 16 MB.", 3);
                    return;
                }

                if (pdfMimeType.includes(file.type) && mb >= 100) {
                    this.$message.error("The document could not be uploaded because it is more than 100 MB.", 3);
                    return;
                }

                this.$message.loading("Image uploading, please wait...", 5);

                let path = `${this.channel.siteId}/whatsapp-business/${this.channel._id}/assets/`;
                let imgUploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "gupshup-file",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });
                const _asset = imgUploadData.data.asset;

                await axios({
                    method: imgUploadData.data.method,
                    url: imgUploadData.data.url,
                    data: file,
                    headers: { "Content-Type": file.type },
                });
                console.log("imgUploadData", {_asset,imgUploadData});

                const asset = {
                    id: _asset.id,
                    url: _asset.v2Format && _asset.cdnUrl
                        ? _asset.cdnUrl
                        : `https://static.supsis.live/${_asset.path}${_asset.id}.${_asset.ext}`,
                    filename: file.name,
                    type: file.type,
                };
                sendSocketMessage("whatsapp upload-file", {
                    asset,
                    siteId: this.channel.siteId,
                    channelId: this.channel._id,
                });

                const exampleMediaId = await new Promise((resolve) => {
                    let timeout = null;
                    const subscription = socketMessageStream.subscribe((message) => {
                        if (message.cmd === "whatsapp_upload_response") {
                            resolve(_.get(message, "data.media.message"));
                            if (timeout) clearTimeout(timeout);
                            subscription.unsubscribe();
                        }
                    });
                    timeout = setTimeout(() => {
                        subscription.unsubscribe();
                        resolve(null);
                    }, 60 * 1000);
                });

                this.submitting = false;
                if (!exampleMediaId) {
                    this.$message.error("Failed to upload image");
                    return;
                }

                this.media = {
                    asset,
                    data: URL.createObjectURL(file),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                };
                this.form.exampleMedia = exampleMediaId;
                this.$message.success("Resim başarılı bir şekilde yüklendi.");
            }
        },
        getExampleBodyContent(content, variables) {
            const variableHash = _.keyBy(variables, "variable");
            return content.replace(/{{\d+}}/g, function(match) {
                return `[${variableHash[match].example}]`;
            });
        },
        listenSocketMessages() {
            this.subscriber = socketMessageStream.subscribe((message) => {
                if (message.cmd === "whatsapp_create_template_message_response") {
                    log("whatsapp_create_template_message_response =>", message);
                    if (!!message.data.response) {
                        this.$message.success("Template message created successfully");
                        this.viewTemplates();
                    } else {
                        this.submitting = false;
                        this.$message.error("Failed to create template message");
                    }
                }
            });
        },

        submit() {
            if (this.checkIsValid()) {
                this.submitting = true;
                this.createTemplate();
                this.$message.loading("Template message creating, please wait...", 3);
            }
        },
        createTemplate() {
            let templateType = this.form.templateType;
            if (this.form.templateType === "CUSTOM_MESSAGE") {
                templateType = this.form.customMessageType === "NONE" ? "TEXT" : this.form.customMessageType;
            }

            const templateMessage = {
                templateType,
                category: this.form.category,
                elementName: this.form.elementName,
                languageCode: this.form.languageCode,
                content: this.form.content,
                appId: this.form.appId,
                vertical: this.form.elementName,
                example: this.getExampleBodyContent(this.form.content, this.bodyVariables),
                allowTemplateCategoryChange: this.form.allowTemplateCategoryChange,
                checkerApprovalRequired: this.form.checkerApprovalRequired,
                enableSample: this.form.enableSample,
                buttons: this.form.buttons.length ? this.form.buttons.map((button) => {
                    if (button.type === "URL") {
                        const payload = { type: button.type, text: button.text };
                        payload.url = button.urlType === "STATIC" ? button.url : button.buttonValue + "{{1}}";
                        payload.buttonValue = button.buttonValue || button.url;
                        if (button.urlType === "DYNAMIC" && button.suffix) {
                            payload.suffix = button.suffix;
                            payload.example = [button.buttonValue + button.suffix];
                        }
                        return payload;
                    }
                    return button;
                }) : undefined,
                header: this.form.customMessageType === "TEXT" && (this.form.header || "").length ? this.form.header : undefined,
                exampleHeader: this.form.customMessageType === "TEXT"  ? this.getExampleBodyContent(this.form.header, this.headerVariables) : undefined,
                exampleMedia: this.form.exampleMedia,
                footer: (this.form.footer || "").length ? this.form.footer : undefined,
                assetId: this.media?.asset?.id,
            };

            if (this.form.templateType === "CAROUSEL") {
                delete templateMessage.header;
                delete templateMessage.footer;
                delete templateMessage.exampleHeader;
                templateMessage.cards = this.carouselCards.map((card) => {
                    return {
                        sampleText: this.getExampleBodyContent(card.body, card.variables),
                        headerType: card.headerType,
                        mediaUrl: card.mediaUrl || card.media?.asset?.url,
                        mediaId: null,
                        exampleMedia: null,
                        body: card.body,
                        buttons: card.buttons.map((button) => {
                            const payload = { type: button.type, text: button.text };
                            if (button.type === "URL") {
                                payload.url = button.buttonValue + button.urlType === "STATIC" ? "" : "{{1}}";
                                payload.buttonValue = button.buttonValue;
                                payload.suffix = button.suffix;
                                payload.example = [
                                    button.buttonValue + button.urlType === "STATIC" ? "" : button.suffix,
                                ];
                            }
                            return payload;
                        }),
                    };
                });
            }

            sendSocketMessage("whatsapp create-template-message", {
                siteId: this.channel.siteId,
                channelId: this.channel._id,
                templateMessage,
            });
        },
        viewTemplates() {
            log("viewTemplates");
            this.changePage(WABusinessMenuTypes.MENU_TYPES.TEMPLATES);
        },

        addVariable() {
            if (this.bodyVariables.length > 4) {
                this.$message.error("You can only add a maximum of 4 variables to the body.");
                return;
            }
            this.form.content = `${this.form.content} {{${this.bodyVariables.length + 1}}}`.slice(0, 1024);
            this.findBodyVariables();
        },
        addButton() {
            let newButton;
            if (this.form.buttonType === "URL") {
                const urlButtonCount = this.form.buttons.filter((btn) => btn.type === "URL").length;
                if (urlButtonCount >= 2) {
                    this.$message.error(this.$t("message.maxUrlButtonMessage"));
                    return;
                }
                newButton = {
                    type: "URL",
                    text: `URL Button ${urlButtonCount + 1}`,
                    url: "",
                    urlType: "STATIC",
                    buttonValue: "",
                    suffix: "",
                };
            } else if (this.form.buttonType === "BAN_CONTACT") {
                newButton = {
                    type: "QUICK_REPLY",
                    text: `#Abonelikten Çıkar#`,
                };
            } else if (this.form.buttonType === "PHONE_NUMBER") {
                const callButtonCount = this.form.buttons.filter((btn) => btn.type === "PHONE_NUMBER").length;
                if (callButtonCount >= 1) {
                    this.$message.error("You can only add a maximum of 1 phone number button.");
                    return;
                }

                newButton = {
                    type: "PHONE_NUMBER",
                    text: `Phone Number Button`,
                    phone_number: "",
                };
            } else {
                const quickReplyCount = this.form.buttons.filter((btn) => btn.type === "QUICK_REPLY").length;
                newButton = {
                    type: "QUICK_REPLY",
                    text: `Button ${quickReplyCount + 1}`,
                };
            }
            this.form.buttons.push(newButton);
            this.selectedButtonIndex = this.form.buttons.length - 1; // Yeni eklenen button'ı seç
            this.sortButtons();
        },
        removeButtonList(buttonToRemove) {
            const buttonIndex = this.form.buttons.findIndex(button => button === buttonToRemove);
            this.form.buttons = this.form.buttons.filter((button) => button !== buttonToRemove);
            
            // selectedButtonIndex'i güncelle
            if (this.form.buttons.length === 0) {
                this.selectedButtonIndex = -1;
            } else if (this.selectedButtonIndex >= buttonIndex) {
                this.selectedButtonIndex = Math.max(0, this.selectedButtonIndex - 1);
            }
            
            this.sortButtons();
        },
        onButtonDragEnd() {
            // Sırala butonları drag sonrası
            this.sortButtons();
        },
        sortButtons() {
            this.form.buttons.sort((a, b) => {
                if (a.type === b.type) {
                    return 0;
                }
                if (a.type === "URL") {
                    return -1;
                }
                if (b.type === "URL") {
                    return 1;
                }
                return a.type.localeCompare(b.type);
            });
        },
        findBodyVariables() {
            const regex = /{{\d+}}/g;
            const matches = _.uniq(this.form.content.match(regex));
            log("findBodyVariables=>", matches);
            if (matches) {
                const getValue = (variable) => this.bodyVariables.find((x) => x.variable === variable)?.example || "";
                this.bodyVariables = matches.map((x, i) => {
                    return {
                        variable: x,
                        example: getValue(x),
                    };
                });
            }
        },
        toggleCard(index) {
            this.carouselCards.forEach((card, i) => {
                if (i !== index) {
                    card.isOpen = false;
                }
            });
            this.carouselCards[index].isOpen = !this.carouselCards[index].isOpen;
        },

        addCard() {
            if (this.carouselCards.length < 10) {
                this.carouselCards.forEach((card) => {
                    card.isOpen = false;
                });

                this.carouselCards.push({
                    isOpen: true,
                    headerType: this.carouselMediaType,
                    media: null,
                    mediaUrl: null,
                    mediaId: null,
                    exampleMedia: null,
                    body: "",
                    sampleText: "",
                    buttons: this.carouselButtons.map((button) => ({
                        ...button,
                        text: "",
                        url: button.type === "URL" ? "" : undefined,
                        buttonValue: button.type === "URL" ? "" : undefined,
                        suffix: button.type === "URL" ? "" : undefined,
                        example: button.type === "URL" ? [""] : undefined,
                    })),
                    variables: [],
                });
            }
        },

        removeCard(index) {
            if (this.carouselCards.length > 1) {
                this.carouselCards.splice(index, 1);
            }
        },

        getAcceptedFileTypes(mediaType) {
            return mediaType === "IMAGE" ? "image/jpeg, image/png" : "video/mp4";
        },

        async handleCardMediaUpload(event, cardIndex) {
            console.log("event test 2", event, cardIndex);
            const file = event.target.files[0];
            if (!file) return;

            const isImage = file.type.startsWith("image/");
            const isVideo = file.type.startsWith("video/");
            const maxSize = isVideo ? 20 : 2;

            const mb = file.size / 1024 / 1024;
            if (mb > maxSize) {
                this.$message.error(`File size should not exceed ${maxSize}MB`);
                return;
            }

            const hide = message.loading("Uploading media...", 0);

            try {
                let path = `${this.channel.siteId}/whatsapp-business/${this.channel._id}/carousel/`;
                let uploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "gupshup-file",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });

                await axios({
                    method: uploadData.data.method,
                    url: uploadData.data.url,
                    data: file,
                    headers: { "Content-Type": file.type },
                });

                const mediaUrl = uploadData.data.asset.v2Format && uploadData.data.asset.cdnUrl
                ? uploadData.data.asset.cdnUrl
                :`https://static.supsis.live/${uploadData.data.asset.path}${uploadData.data.asset.id}.${uploadData.data.asset.ext}`;
                const asset = {
                    id: uploadData.data.asset.id,
                    url: mediaUrl,
                    filename: file.name,
                    type: file.type,
                };

                this.carouselCards = this.carouselCards.map((card, i) => {
                    if (i !== cardIndex) return card;
                    return {
                        ...card,
                        mediaUrl,
                        headerType: this.carouselMediaType,
                        mediaId: uploadData.data.asset.id,
                        media: {
                            data: URL.createObjectURL(file),
                            name: file.name,
                            size: file.size,
                            type: file.type,
                            asset,
                        },
                    };
                });

                this.$message.success("Media uploaded successfully", 2);
            } catch (error) {
                this.$message.error("Failed to upload media", 2);
                console.error("Upload error:", error);
            } finally {
                hide();
            }
        },

        removeCardMedia(cardIndex) {
            this.carouselCards = this.carouselCards.map((card, i) => {
                if (i !== cardIndex) return card;
                return {
                    ...card,
                    media: null,
                    mediaUrl: null,
                    mediaId: null,
                    exampleMedia: null,
                };
            });
        },

        addCardVariable(cardIndex) {
            this.carouselCards = this.carouselCards.map((c, i) => {
                if (i !== cardIndex) return c;
                return {
                    ...c,
                    body: (c.body + ` {{${c.variables.length + 1}}}`).slice(0, 160),
                };
            });
            this.cardBodyChanged(cardIndex);
        },

        handleMediaTypeChange(value) {
            this.form.customMessageType = value;
            this.carouselCards.forEach((card) => {
                card.headerType = value;
                card.media = null;
                card.mediaUrl = null;
                card.mediaId = null;
                card.exampleMedia = null;
            });
        },

        removeGlobalButton() {
            if (this.carouselButtons.length > 1) {
                this.carouselButtons.pop();
                this.carouselCards.forEach((card) => {
                    card.buttons.pop();
                });
            }
        },

        addGlobalButton() {
            if (this.carouselButtons.length < 2) {
                const newButton = {
                    type: "QUICK_REPLY",
                };
                this.carouselButtons.push(newButton);
                this.carouselCards.forEach((card) => {
                    card.buttons.push({
                        ...newButton,
                        text: "",
                        url: "",
                    });
                });
            }
        },
        updateCardButtons() {
            this.carouselCards.forEach((card) => {
                card.buttons = this.carouselButtons.map((globalButton, index) => ({
                    ...globalButton,
                    text: card.buttons[index]?.text || "",
                    url: card.buttons[index]?.url || "",
                }));
            });
        },
        addHeaderVariable() {
            if (this.headerVariables.length >= 1) {
                this.$message.error("You can add up to one variable");
            } else {
                this.headerVariables.push({
                    variable: `{{${this.headerVariables.length + 1}}}`,
                    example: "",
                });
                this.form.header = `${this.form.header} {{${this.headerVariables.length}}}`.slice(0, 60);
            }
        },
    },
    mounted() {
        this.buttonTypes = [...this.staticButtonTypes];
        this.listenSocketMessages();
    },
    beforeDestroy() {
        this.subscriber.unsubscribe();
    },

    watch: {
        form: {
            handler: function(newValue, oldValue) {
                if (!!newValue) {
                    this.findBodyVariables();
                }
            },
            deep: true,
            immediate: true,
        },
        "form.elementName": function(newValue) {
            const regex = /^[a-z0-9-_]+$/;
            this.elementNameError = !regex.test(newValue);
        },
    },
});
</script>
<style scoped lang="scss">
.error {
    border: 2px solid red !important;
}

.tab-ne-template-message-form {
    display: flex;
    flex-direction: column;
    gap: 40px;
    padding: 20px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 10px;
        border-bottom: 1px solid var(--color-border-default);
        border-radius: 4px;

        .title {
            font-size: 18px;
            font-weight: 700;
            color: var(--color-text-strong-default);
        }

        .subtitle {
            font-size: 14px;
            color: var(--color-text-strong-default);
        }
    }

    .template-message-form-container {
        display: flex;
        gap: 40px;
    }

    .inputs {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 30px;

        .input-wrapper {
            display: flex;
            flex-direction: column;
            gap: 5px;
            padding: 25px;
            border: 1px solid var(--color-border-default);
            border-radius: 8px;

            .input-header {
                display: flex;
                align-items: center;

                label {
                    font-size: 18px;
                    font-weight: 700;
                    color: var(--color-text-strong-default);
                    display: flex;
                    justify-content: space-between;
                    width: 100%;
                    padding-right: 20px;
                }

                .optional {
                    font-size: 10px;
                    font-weight: 400;
                    color: var(--color-text-low-default);
                    background: var(--color-bg-neutral-default);
                    padding: 1px 6px;
                    border-radius: 3px;
                }

                .required {
                    font-size: 10px;
                    font-weight: 400;
                    color: var(--color-text-onprimary-default);
                    background: var(--color-bg-critical-default);
                    padding: 1px 6px;
                    border-radius: 3px;
                }
            }

            .input-warn {
                background: #fff6e6;
                padding: 20px;

                .list-item {
                    padding-left: 15px;
                }
            }

            .media-preview {
                display: flex;
                align-items: center;
                justify-content: space-between;

                .media-preview-content {
                    display: flex;
                    align-items: center;
                    gap: 10px;

                    .media-preview-name {
                        font-weight: bold;
                    }

                    .media-image img {
                        height: 100px;
                    }
                }

                .media-preview-delete {
                    background: red;
                    padding: 5px;
                    border-radius: 100%;
                    color: white;
                    line-height: 0;
                    cursor: pointer;
                }
            }

            .media-drag {
                display: flex;
                align-items: center;
                flex-direction: column;
                gap: 10px;
                border: 1px solid var(--color-border-default);
                padding: 40px;
                cursor: pointer;
                position: relative;

                .input {
                    width: 100%;
                    height: 100%;

                    input {
                        width: 100%;
                        height: 100%;
                        position: absolute;
                        top: 0;
                        left: 0;
                        opacity: 0;
                    }
                }

                .material-icons {
                    font-size: 28px;
                }

                .content {
                    font-size: 18px;
                    font-weight: bold;
                }
            }

            .description {
                color: var(--color-text-low-default);
                font-size: 14px;
            }

            .input-desc {
                color: var(--color-text-low-default);
                font-size: 12px;
            }

            .categories {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                gap: 20px;

                .category {
                    margin: 0;
                    width: 280px;
                    height: 115px;
                    padding: 20px;
                    align-items: flex-start;
                    position: relative;
                    background: #f9fafb;
                    display: flex;
                    flex-direction: column;
                    gap: 5px;
                    cursor: pointer;

                    .category-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        width: 100%;

                        .left {
                            display: flex;
                            gap: 5px;
                            align-items: center;
                            width: 100%;

                            .label {
                                font-size: 14px;
                                font-weight: 700;
                                color: var(--color-text-strong-default);
                            }
                        }

                        .right {
                            width: 100%;
                            display: flex;
                            justify-content: flex-end;
                            align-items: center;
                        }
                    }

                    .category-description {
                        font-size: 12px;
                        color: var(--color-text-low-default);
                    }
                }
            }

            .category-types {
                display: flex;
                flex-direction: column;
                gap: 10px;
                padding: 20px;
                background: #f9fafb;

                .category-type {
                    &-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        width: 100%;

                        .left {
                            display: flex;
                            gap: 5px;
                            align-items: center;
                            width: 100%;

                            .label {
                                font-size: 14px;
                                font-weight: 700;
                                color: var(--color-text-strong-default);
                            }
                        }

                        .right {
                            width: 100%;
                            display: flex;
                            justify-content: flex-end;
                            align-items: center;
                        }
                    }

                    &-description {
                        font-size: 12px;
                        color: var(--color-text-low-default);
                    }
                }
            }

            .input-footer {
                display: flex;
                justify-content: space-between;

                .counter {
                    display: flex;
                    gap: 3px;
                }
            }

            .variables-container {
                display: flex;
                flex-direction: column;
                gap: 5px;
                width: 66%;
                margin-top: 15px;

                .labels {
                    width: 100%;
                    display: flex;

                    #labelSecond {
                        margin-left: 40%;
                        margin-right: 8px;
                    }
                }

                label {
                    font-size: 14px;
                    font-weight: 700;
                    color: var(--color-text-strong-default);
                }

                .variables {
                    display: flex;
                    flex-direction: column;
                    gap: 5px;

                    .variable {
                        display: flex;
                        gap: 20px;
                    }
                }
            }

            .add-variable-button {
                margin-top: 10px;
            }

            .button-container {
                display: flex;
                flex-direction: column;
                gap: 16px;

                label {
                    font-size: 14px;
                    font-weight: 700;
                    color: var(--color-text-strong-default);
                }
            }

            .button-management {
                display: grid;
                grid-template-columns: 1fr 2fr;
                gap: 24px;
                min-height: 300px;

                @media (max-width: 768px) {
                    grid-template-columns: 1fr;
                }
            }

            .button-preview-section {
                .button-previews {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .button-preview-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 12px;
                    border: 2px solid #e5e7eb;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: all 0.2s;

                    &:hover {
                        border-color: #d1d5db;
                    }

                    &.active {
                        border-color: #4f46e5;
                        background-color: #f8fafc;
                        box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
                    }

                    .button-preview {
                        display: flex;
                        flex-direction: column;
                        gap: 4px;

                        .button-type-badge {
                            font-size: 10px;
                            padding: 2px 6px;
                            background-color: #e5e7eb;
                            border-radius: 4px;
                            color: #6b7280;
                            text-align: center;
                        }
                    }

                    .button-preview-actions {
                        display: flex;
                        gap: 4px;

                        .material-icons {
                            font-size: 18px;
                            color: #6b7280;
                            cursor: pointer;
                            padding: 4px;
                            border-radius: 4px;

                            &:hover {
                                background-color: #f3f4f6;
                            }

                            &.drag-handle {
                                cursor: grab;
                                
                                &:hover {
                                    color: #4f46e5;
                                    background-color: #eef2ff;
                                }

                                &:active {
                                    cursor: grabbing;
                                }
                            }

                            &.remove-button:hover {
                                color: #ef4444;
                                background-color: #fef2f2;
                            }
                        }
                    }
                }
            }

            .button-config-section {
                background-color: #f9fafb;
                border-radius: 12px;
                padding: 20px;
                border: 1px solid #e5e7eb;

                .config-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 20px;
                    padding-bottom: 12px;
                    border-bottom: 1px solid #e5e7eb;

                    h3 {
                        margin: 0;
                        font-size: 16px;
                        font-weight: 600;
                        color: #1f2937;
                    }

                    .button-type {
                        padding: 4px 12px;
                        background-color: #4f46e5;
                        color: white;
                        border-radius: 16px;
                        font-size: 12px;
                        font-weight: 600;
                    }
                }

                .button-config-form {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;

                    .config-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 16px;

                        @media (max-width: 768px) {
                            grid-template-columns: 1fr;
                        }
                    }

                    .config-field {
                        display: flex;
                        flex-direction: column;
                        gap: 6px;

                        label {
                            font-size: 13px;
                            font-weight: 600;
                            color: #374151;
                            margin: 0;
                        }

                        .field-info {
                            font-size: 11px;
                            color: #6b7280;
                            text-align: right;
                        }
                    }

                    .dynamic-config {
                        margin-top: 8px;
                        padding: 16px;
                        background-color: white;
                        border-radius: 8px;
                        border: 1px solid #e5e7eb;

                        .dynamic-info {
                            display: flex;
                            align-items: flex-start;
                            gap: 8px;
                            margin-bottom: 16px;
                            padding: 12px;
                            background-color: #fef3cd;
                            border-radius: 8px;

                            .material-icons {
                                font-size: 16px;
                                color: #d97706;
                            }

                            span:not(.material-icons) {
                                font-size: 12px;
                                color: #92400e;
                                line-height: 1.4;
                            }
                        }

                        .dynamic-input-group {
                            display: flex;
                            align-items: center;
                            gap: 12px;

                            .variable-tag {
                                background-color: #4f46e5;
                                color: white;
                                padding: 8px 12px;
                                border-radius: 2px;
                                font-weight: 600;
                                font-size: 13px;
                                min-width: 45px;
                                text-align: center;
                            }
                        }
                    }
                }
            }
        }
    }

    .right {
        display: flex;
        position: relative;

        .preview-container {
            position: sticky;
            top: 20px;
            max-height: 600px;
        }
    }
}

.carousel-card {
    border: 1px solid var(--color-border-default);
    border-radius: 8px;
    margin-bottom: 10px;

    .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 16px;
        background-color: #f5f5f5;
        cursor: pointer;
        border-radius: 8px 8px 0 0;

        .left {
            display: flex;
            align-items: center;
            gap: 10px;

            .drag-handle {
                cursor: move;
                color: #999;
            }

            .carousel-card-heading {
                font-weight: 600;
            }
        }

        .right {
            display: flex;
            align-items: center;
            gap: 10px;

            .delete-btn {
                color: var(--color-text-critical-default);
            }
        }
    }

    .card-content {
        padding: 16px;

        .section-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;

            label {
                font-weight: 600;
            }
        }

        .media-section,
        .description-section,
        .buttons-section {
            margin-bottom: 20px;
        }

        .button-list {
            margin: 10px 0;

            .button-inputs-row {
                display: flex;
                gap: 10px;
                align-items: flex-start;

                .sample-url-inputs {
                    display: flex;
                    align-items: center;
                    gap: 5px;

                    span {
                        font-weight: 500;
                        font-size: 17px;
                    }
                }
            }

            .button-item {
                margin-bottom: 10px;
                display: flex;
                flex-direction: column;
                gap: 10px;

                .button-type {
                    width: 150px;
                    display: flex;
                    align-items: center;
                }

                .button-inputs {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .button-delete {
                    padding: 8px;
                    cursor: pointer;
                    color: var(--color-text-critical-default);
                    display: flex;
                    align-items: center;
                }
            }
        }
    }
}

.carousel-actions {
    margin-top: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .counter {
        color: var(--color-text-low-default);
    }
}

.global-settings {
    margin-bottom: 24px;
    padding: 24px;
    border: 1px solid var(--color-border-default);
    border-radius: 8px;
    background-color: #f9fafb;

    .section-header {
        margin-bottom: 16px;
        display: flex;
        align-items: center;
        gap: 8px;

        label {
            font-size: 16px;
            font-weight: 600;
            color: var(--color-text-strong-default);
        }

        .required,
        .optional {
            font-size: 12px;
            padding: 2px 8px;
            border-radius: 4px;
        }

        .required {
            background-color: var(--color-bg-critical-default);
            color: white;
        }

        .optional {
            background-color: var(--color-bg-neutral-default);
            color: var(--color-text-low-default);
        }
    }

    .media-type-section {
        margin-bottom: 24px;

        .input-warn {
            margin-top: 12px;
            padding: 16px;
            background: #fff6e6;
            border-radius: 4px;
            font-size: 14px;

            b {
                color: var(--color-text-strong-default);
            }

            .list-item {
                margin-top: 8px;
                padding-left: 20px;

                li {
                    margin-bottom: 4px;
                    color: var(--color-text-low-default);
                }
            }
        }
    }

    .button-config-section {
        .button-config {
            display: flex;
            flex-direction: column;
            gap: 16px;

            .button-type-config {
                background: white;
                padding: 16px;
                border-radius: 8px;
                border: 1px solid var(--color-border-default);
                display: flex;
                gap: 16px;
                align-items: center;

                .button-type {
                    flex: 1;

                    .button-label {
                        display: block;
                        margin-bottom: 8px;
                        font-weight: 500;
                        color: var(--color-text-strong-default);
                    }
                }

                .button-delete {
                    .ant-btn-link {
                        color: var(--color-text-critical-default);
                    }
                }
            }

            .button-add {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin-top: 8px;

                .counter {
                    color: var(--color-text-low-default);
                }
            }
        }
    }
}

.carousel-cards-section {
    .carousel-card {
        margin-bottom: 16px;
        border: 1px solid var(--color-border-default);
        border-radius: 8px;
        overflow: hidden;

        .card-header {
            background: #f5f5f5;
            padding: 12px 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            cursor: pointer;

            .left {
                display: flex;
                align-items: center;
                gap: 12px;

                .drag-handle {
                    cursor: move;
                    color: #999;
                }

                .carousel-card-heading {
                    font-weight: 600;
                }
            }

            .right {
                display: flex;
                align-items: center;
                gap: 12px;

                .delete-btn {
                    color: var(--color-text-critical-default);
                }
            }
        }

        .card-content {
            padding: 16px;

            .buttons-section {
                margin-top: 24px;

                .button-list {
                    margin-top: 16px;

                    .button-item {
                        background: #f9fafb;
                        padding: 16px;
                        border-radius: 8px;
                        margin-bottom: 16px;
                        border: 1px solid var(--color-border-default);

                        .button-info {
                            margin-bottom: 12px;

                            .button-type-label {
                                display: inline-block;
                                font-weight: 500;
                                font-size: 12px;
                                padding: 4px 8px;
                                border-radius: 4px;
                                background: #e6e6e6;
                                color: var(--color-text-strong-default);
                            }
                        }

                        .button-inputs {
                            display: flex;
                            flex-direction: column;
                            gap: 12px;

                            .input-footer {
                                display: flex;
                                justify-content: flex-end;

                                .counter {
                                    font-size: 12px;
                                    color: var(--color-text-low-default);
                                }
                            }

                            .ant-input {
                                border-color: var(--color-border-default);

                                &:focus {
                                    border-color: var(--color-border-primary-default);
                                    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

.carousel-actions {
    margin-top: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .counter {
        color: var(--color-text-low-default);
    }
}

.description-section {
    margin-bottom: 24px;

    .section-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;

        label {
            font-weight: 600;
        }

        .required {
            font-size: 10px;
            font-weight: 400;
            color: var(--color-text-onprimary-default);
            background: var(--color-bg-critical-default);
            padding: 1px 6px;
            border-radius: 3px;
        }
    }

    .description-footer {
        margin-top: 8px;
        display: flex;
        justify-content: flex-end;
    }

    .variable-button {
        margin-top: 16px;
    }
}

.button-texts-section {
    .section-header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;

        label {
            font-weight: 600;
        }

        .required {
            font-size: 10px;
            font-weight: 400;
            color: var(--color-text-onprimary-default);
            background: var(--color-bg-critical-default);
            padding: 1px 6px;
            border-radius: 3px;
        }
    }
}

.button-group-heading {
    font-weight: 600;
}

</style>
